'use client';
import './i18n/autoTranslate';
import LanguageSwitcher from './i18n/LanguageSwitcher';
import { getLanguage, subscribeToLanguage } from './i18n/autoTranslate';

import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  BarChart3,
  BookOpen,
  CalendarDays,
  FileBox,
  HelpCircle,
  LayoutDashboard,
  List,
  Lock,
  LogOut,
  Play,
  Settings,
  CreditCard,
  ArrowUpRight,
  Crown,
  Sparkles,
  ShieldCheck,
  Zap,
  TriangleAlert,
  Activity,
  LucideFileArchive,
  FileBadge,
  Trophy,
  Medal,
  Landmark,
  Briefcase
} from 'lucide-react';
import { Separator } from '../components/ui/shadcn/separator';
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from '../components/ui/Sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/shadcn/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/shadcn/tooltip';
import { useIsMobile } from '../hooks/use-mobile';
import { logout } from '../features/auth/authSlice';
import { startTour } from '../utils/Tour';
import { RiChatAiFill } from 'react-icons/ri';
import AppBackground from '../components/ui/AppBackground.tsx';

import AppIcon from '../components/AppIcon';

/* -------------------------- Nav config -------------------------- */
const NAV_ITEMS = [
  { id: 'nav-dashboard', title: 'Dashboard', path: '/dashboard-home', icon: LayoutDashboard },
  { id: 'nav-sessions-planner', title: 'Sessions Planner', path: '/upcoming-sessions-planner', icon: CalendarDays },
  { id: 'nav-ai-interview', title: 'AI Interview', path: '/interview-setup-configuration', icon: Play },
  { id: 'nav-chat-interview', title: 'AI Chat', path: '/chat-interview', icon: RiChatAiFill, isReactIcon: true, lockedForFree: true },
  { id: 'nav-questions-ai', title: 'Questions AI', path: '/generate-questions', icon: List, lockedForFree: true },
  { id: 'nav-library', title: 'Library', path: '/practice-library-resources', icon: BookOpen, lockedForFree: true },
  { id: 'nav-analytics', title: 'Analytics', path: '/progress-analytics-insights', icon: BarChart3 },
  { id: 'nav-resume-ai', title: 'Resume AI', path: '/resume-builder', icon: FileBox, lockedForFree: true },
  { id: 'nav-ats-optimization', title: 'ATS Optimization', path: '/salaryboost', icon: LucideFileArchive, lockedForFree: true },
  { id: 'nav-pdf-editor', title: 'PDF Editor', path: '/pdf-editor', icon: FileBadge, lockedForFree: true },
  { id: 'nav-questions-bank', title: 'Questions Bank', path: '/questions-bank', icon: Landmark, lockedForFree: true },
  { id: 'nav-job-match', title: 'JobMatch AI', path: '/job-match-ai', icon: Briefcase, lockedForFree: true },
  
];

const FREE_LOCKED_NAV_IDS = new Set(NAV_ITEMS.filter((i) => i.lockedForFree).map((i) => i.id));
const FREE_LOCKED_PATHS = NAV_ITEMS.filter((i) => i.lockedForFree).map((i) => i.path);
const isFreeLockedPath = (pathname = '') =>
  FREE_LOCKED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const SECONDARY_ITEMS = [
  { id: 'menu-settings', title: 'Settings', path: '/setting', icon: Settings },
  { id: 'menu-help', title: 'Help', path: '/help', icon: HelpCircle },
];

const PATH_TO_HEADER_INFO = {
  '/dashboard-home': { label: 'Dashboard', icon: 'LayoutDashboard' },
  '/upcoming-sessions-planner': { label: 'Sessions Planner', icon: 'CalendarDays' },
  '/interview-setup-configuration': { label: 'Interview Setup', icon: 'Settings' },
  '/live-interview-session': { label: 'Live Session', icon: 'Video' },
  '/interview-results-feedback': { label: 'Results & Feedback', icon: 'BarChart3' },
  '/practice-library-resources': { label: 'Practice Library', icon: 'BookOpen' },
  '/progress-analytics-insights': { label: 'Analytics & Insights', icon: 'BarChart3' },
  '/resume-builder': { label: 'Resume AI', icon: 'FileBox' },
  '/chat-interview': { label: 'AI Chat', icon: 'MessageSquare' },
  '/generate-questions': { label: 'Questions AI', icon: 'List' },
  '/setting': { label: 'Settings', icon: 'Settings' },
  '/help': { label: 'Help', icon: 'HelpCircle' },
  '/leaderboard': { label: 'Leaderboard', icon: 'Trophy' },
};

const getPageHeaderInfo = (pathname) => {
  if (PATH_TO_HEADER_INFO[pathname]) return PATH_TO_HEADER_INFO[pathname];
  const item =
    NAV_ITEMS.find((i) => pathname === i.path) || NAV_ITEMS.find((i) => pathname?.startsWith(i.path));
  if (item) return { label: item.title, iconComponent: item.icon, isReactIcon: item.isReactIcon };
  const sec = SECONDARY_ITEMS.find((i) => pathname === i.path);
  if (sec) return { label: sec.title, iconComponent: sec.icon };
  if (pathname === '/admin') return { label: 'Admin', icon: 'Shield' };
  if (pathname === '/phone-interview') return { label: 'Phone Interview', icon: 'Phone' };
  if (pathname === '/live-interview-session') return { label: 'Live Interview', icon: 'Video' };
  if (pathname === '/interview-results-feedback') return { label: 'Results', icon: 'BarChart3' };
  if (
    pathname === '/my-subscription' ||
    pathname === '/my-subscription/usage' ||
    pathname === '/my-invoices' ||
    pathname === '/my-payments' ||
    pathname === '/plans'
  ) return { label: 'Billing & Subscriptions', icon: 'CreditCard' };
  if (pathname === '/issue-report') return { label: 'Reports and issues', icon: 'TriangleAlert' };
  return { label: 'InterviewGenieAI', icon: 'Sparkles' };
};

/* -------------------------- Plan UI -------------------------- */

const PLAN_META = {
  free: {
    name: 'Free', tag: 'Starter', icon: Sparkles,
    accent: 'from-slate-400/25 via-white/10 to-slate-400/10',
    glow: 'shadow-[0_18px_50px_-30px_rgba(148,163,184,0.45)]',
    perks: ['Limited interviews', 'Basic feedback', 'Community support'],
    cta: 'Upgrade',
  },
  starter: {
    name: 'Starter', tag: 'Good start', icon: Zap,
    accent: 'from-emerald-400/25 via-white/10 to-cyan-400/10',
    glow: 'shadow-[0_18px_50px_-30px_rgba(34,197,94,0.45)]',
    perks: ['More interviews', 'Better analytics', 'Email support'],
    cta: 'Upgrade',
  },
  pro: {
    name: 'Pro', tag: 'Most popular', icon: Crown,
    accent: 'from-indigo-500/30 via-purple-500/15 to-cyan-400/10',
    glow: 'shadow-[0_20px_60px_-34px_rgba(99,102,241,0.7)]',
    perks: ['Unlimited interviews', 'Advanced scorecards', 'Priority support'],
    cta: 'Manage',
  },
  executive: {
    name: 'Executive', tag: 'Teams', icon: ShieldCheck,
    accent: 'from-amber-400/25 via-white/10 to-indigo-500/10',
    glow: 'shadow-[0_20px_60px_-34px_rgba(245,158,11,0.55)]',
    perks: ['Team seats', 'Admin analytics', 'SLA support'],
    cta: 'Manage',
  },
};

/* ──────────────────────────────────────────────────────────────────
   Usage Mini-Widget — responsive
────────────────────────────────────────────────────────────────── */

function UsageMiniWidget({ planKey }) {
  const [open, setOpen] = React.useState(false);
  const [usage, setUsage] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const ref = React.useRef(null);

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
  const getAccessToken = () =>
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';

  const fetchJSON = async (path, token, opts = {}) => {
    const res = await fetch(`${API_URL}${path}`, {
      ...opts,
      headers: {
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    const ct = res.headers.get('content-type') || '';
    const data = ct.includes('application/json') ? await res.json() : { raw: await res.text() };
    if (!res.ok) {
      const err = new Error(data?.error || data?.message || `Request failed (${res.status})`);
      err.status = res.status; err.data = data; throw err;
    }
    return data;
  };

  React.useEffect(() => {
    let cancelled = false;
    const token = getAccessToken();
    if (!token) return;
    (async () => {
      setLoading(true);
      try {
        const usageRes = await fetchJSON('/api/subscriptions/my-subscription/usage', token);
        if (!cancelled) setUsage(usageRes.usage || null);
      } catch (e) {
        console.error('[UsageMiniWidget] error', e);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const pct       = Number(usage?.usage_percentage ?? 0);
  const used      = Number(usage?.interviews_used  ?? 0);
  const limit     = Number(usage?.interviews_limit ?? 0);
  const remaining = Number(usage?.interviews_remaining ?? Math.max(0, limit - used));
  const resetDate = usage?.reset_date ?? null;
  const daysLeft  = usage?.days_until_reset ?? null;
  const canUse    = usage?.can_use_interview ?? null;
  const hasSub    = Boolean(usage);

  const isNearCap = pct >= 80 && pct < 100;
  const isAtCap   = pct >= 100;

  const R = 9; const CIRC = 2 * Math.PI * R;
  const dash      = hasSub ? CIRC * (1 - Math.min(pct, 100) / 100) : CIRC;
  const ringColor = isAtCap ? '#f87171' : isNearCap ? '#fbbf24' : '#818cf8';

  const _fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const clamped = Math.max(0, Math.min(100, pct));
  const conicBg = `conic-gradient(rgba(99,102,241,.95) ${clamped}%, rgba(255,255,255,.10) 0)`;

  const EligPill = ({ ok }) => {
    const cls = ok
      ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/20'
      : 'bg-amber-500/15 text-amber-200 border-amber-500/20';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${cls}`}>
        {ok ? 'Ready' : 'Limit reached'}
      </span>
    );
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger — responsive: ring-only on small, ring+counter on md+ */}
      <button
        onClick={() => setOpen((v) => !v)}
        title="Monthly usage"
        className={[
          'flex items-center gap-1.5 sm:gap-2 rounded-xl border px-2 sm:px-3 py-1.5 text-xs font-semibold transition shrink-0',
          'bg-white/[0.04] backdrop-blur-xl hover:bg-white/[0.08]',
          isAtCap   ? 'border-red-500/35   text-red-300'
          : isNearCap ? 'border-amber-500/35 text-amber-300'
          :             'border-white/10     text-white/75',
        ].join(' ')}
      >
        {hasSub && !loading ? (
          <svg width="20" height="20" viewBox="0 0 24 24" className="shrink-0">
            <circle cx="12" cy="12" r={R} fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="3" />
            <circle
              cx="12" cy="12" r={R} fill="none"
              stroke={ringColor} strokeWidth="3"
              strokeDasharray={CIRC} strokeDashoffset={dash}
              strokeLinecap="round" transform="rotate(-90 12 12)"
              style={{ transition: 'stroke-dashoffset .5s ease' }}
            />
          </svg>
        ) : (
          <Activity className="h-3.5 w-3.5 opacity-60" />
        )}

        <span className="hidden md:inline">
          {loading ? 'Usage…' : hasSub ? `${used} / ${limit}` : 'Usage'}
        </span>

        {isNearCap && <span className="text-amber-400 hidden sm:inline">⚡</span>}
        {isAtCap   && <span className="text-red-400 hidden sm:inline">!</span>}
      </button>

      {/* Popover — responsive width, never clips off-screen */}
      {open && (
        <div className="
          absolute right-0 top-[calc(100%+8px)] z-50
          w-[min(340px,calc(100vw-1rem))]
          rounded-2xl border border-white/10 bg-[#07090c]
          shadow-[0_35px_90px_-40px_rgba(99,102,241,0.7)] overflow-hidden
        ">
          <div className="relative px-5 pt-5 pb-4 border-b border-white/[0.07]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(99,102,241,0.18),transparent_55%),radial-gradient(circle_at_80%_0%,rgba(168,85,247,0.14),transparent_55%)]" />
            <div className="relative flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-indigo-300" />
              <span className="text-[11px] font-semibold uppercase tracking-wider text-white/60">Monthly usage</span>
            </div>
            <div className="relative mt-1 text-base font-bold text-white">Usage &amp; Limits</div>
          </div>

          {loading ? (
            <div className="flex items-center gap-3 px-5 py-6 text-white/50 text-sm">
              <Activity className="h-4 w-4 animate-pulse" /> Loading usage…
            </div>
          ) : !hasSub ? (
            <div className="px-5 py-6 text-sm text-white/50 text-center">No usage data available.</div>
          ) : (
            <>
              <div className="px-4 pt-4 grid grid-cols-3 gap-2">
                {[
                  { label: 'Used',      val: used },
                  { label: 'Remaining', val: remaining },
                  { label: 'Limit',     val: limit },
                ].map(({ label, val }) => (
                  <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.03] px-2 sm:px-3 py-2.5 text-center">
                    <div className="text-[11px] text-white/50">{label}</div>
                    <div className="mt-1 text-lg font-bold text-white tabular-nums">{val}</div>
                  </div>
                ))}
              </div>

              <div className="px-5 pt-5">
                <div className="flex items-center gap-4 sm:gap-5">
                  <div className="relative h-[88px] w-[88px] shrink-0 rounded-full" style={{ background: conicBg }}>
                    <div className="absolute inset-[8px] rounded-full bg-[#07090c] border border-white/10" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-lg font-bold text-white">{clamped.toFixed(0)}%</div>
                        <div className="text-[10px] text-white/50">used</div>
                      </div>
                    </div>
                    <div className="pointer-events-none absolute -inset-4 rounded-full bg-indigo-500/10 blur-2xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-2.5 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
                        style={{ width: `${clamped}%` }}
                      />
                    </div>
                    <div className="mt-2 flex items-center justify-between text-xs text-white/50">
                      <span>{clamped.toFixed(0)}%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-4 pt-4 pb-1 grid grid-cols-2 gap-2">
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 min-w-0">
                  <div className="text-[11px] text-white/50">Reset date</div>
                  <div className="mt-1 text-xs font-semibold text-white truncate">{_fmtDate(resetDate)}</div>
                  {daysLeft !== null && (
                    <div className="mt-1 text-[11px] text-white/45">{daysLeft} days remaining</div>
                  )}
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 min-w-0">
                  <div className="text-[11px] text-white/50">Eligibility</div>
                  <div className="mt-2">
                    {canUse !== null && <EligPill ok={canUse} />}
                  </div>
                  <div className="mt-1.5 text-[11px] text-white/45">
                    {canUse === true  ? 'You can start a new interview.'
                    : canUse === false ? 'Limit reached for this period.'
                    :                   '—'}
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="px-4 py-4">
            <Link
              to="/my-subscription/usage"
              onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-2 h-9 w-full rounded-xl text-xs font-semibold text-white
                bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500
                ring-1 ring-white/10 transition"
            >
              Full Usage Details
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}


/* -------------------------- production sidebar fallback -------------------------- */
function CollapsedSidebarFixStyles() {
  return (
    <style>{`
      :root {
        --ig-sidebar-width: 17rem;
        --ig-sidebar-width-icon: 4rem;
      }

      html,
      body,
      #root {
        max-width: 100%;
        overflow-x: hidden;
      }

      /* Keep the dashboard shell layout LTR even when Arabic is selected.
         The page content can still render RTL, but the sidebar spacer/header order
         no longer collapses or overlaps the dashboard. */
      [data-ig-sidebar-shell],
      [data-ig-app-sidebar],
      [data-ig-app-header] {
        direction: ltr !important;
      }

      [data-ig-page-content][dir="rtl"] {
        direction: rtl;
      }

      /* Hard guard for the collapsed/icon sidebar.
         In production builds the internal shadcn sidebar can keep the expanded
         17rem fixed panel even while labels are hidden, which creates the big
         dark empty block shown on mobile/tablet. This explicit mode shrinks the
         provider variable, the wrapper, the spacer and the fixed sidebar layer. */
      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] {
        --sidebar-width: var(--ig-sidebar-width-icon) !important;
      }

      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-ig-app-sidebar],
      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar="sidebar"] {
        width: var(--ig-sidebar-width-icon) !important;
        min-width: var(--ig-sidebar-width-icon) !important;
        max-width: var(--ig-sidebar-width-icon) !important;
        flex-basis: var(--ig-sidebar-width-icon) !important;
        overflow: hidden !important;
      }

      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar="sidebar"] {
        left: 0 !important;
        right: auto !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar="rail"] {
        display: none !important;
      }

      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar-collapse-label],
      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar-collapse-lock] {
        display: none !important;
      }

      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar-collapse-center],
      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar-collapse-content] {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      [data-ig-sidebar-shell][data-sidebar-icon-mode="true"] [data-sidebar-collapse-menu-button] {
        display: grid !important;
        place-items: center !important;
        justify-content: center !important;
        align-items: center !important;
        gap: 0 !important;
        overflow: hidden !important;
        width: 2.75rem !important;
        min-width: 2.75rem !important;
        max-width: 2.75rem !important;
        height: 2.75rem !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin-left: auto !important;
        margin-right: auto !important;
      }

      @media (max-width: 1024px) {
        [data-ig-sidebar-shell] {
          --sidebar-width: var(--ig-sidebar-width-icon) !important;
        }

        [data-ig-sidebar-shell] [data-ig-app-sidebar],
        [data-ig-sidebar-shell] [data-sidebar="sidebar"] {
          width: var(--ig-sidebar-width-icon) !important;
          min-width: var(--ig-sidebar-width-icon) !important;
          max-width: var(--ig-sidebar-width-icon) !important;
          flex-basis: var(--ig-sidebar-width-icon) !important;
          overflow: hidden !important;
        }
      }

      [data-ig-app-sidebar] {
        width: var(--ig-sidebar-width) !important;
        min-width: var(--ig-sidebar-width) !important;
        max-width: var(--ig-sidebar-width) !important;
        overflow: hidden !important;
      }

      [data-collapsible="icon"][data-ig-app-sidebar],
      [data-state="collapsed"][data-ig-app-sidebar] {
        width: var(--ig-sidebar-width-icon) !important;
        min-width: var(--ig-sidebar-width-icon) !important;
        max-width: var(--ig-sidebar-width-icon) !important;
      }


      [data-ig-app-sidebar] {
        isolation: isolate;
      }

      /* Collapsed sidebar polish: remove the extra visual gutter that appeared
         beside the closed sidebar in production/local builds. The shadcn rail is
         useful while expanded, but in icon mode it creates a dead strip on the
         right; hiding it makes the icon rail feel tight and intentional. */
      [data-collapsible="icon"] [data-sidebar="rail"],
      [data-state="collapsed"] [data-sidebar="rail"] {
        display: none !important;
      }

      [data-collapsible="icon"] [data-sidebar="sidebar"],
      [data-state="collapsed"] [data-sidebar="sidebar"] {
        width: var(--ig-sidebar-width-icon) !important;
        min-width: var(--ig-sidebar-width-icon) !important;
        max-width: var(--ig-sidebar-width-icon) !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      [data-collapsible="icon"] [data-sidebar="content"],
      [data-state="collapsed"] [data-sidebar="content"],
      [data-collapsible="icon"] [data-sidebar="footer"],
      [data-state="collapsed"] [data-sidebar="footer"],
      [data-collapsible="icon"] [data-sidebar="header"],
      [data-state="collapsed"] [data-sidebar="header"] {
        width: 100% !important;
      }

      [data-collapsible="icon"] [data-sidebar="menu"],
      [data-state="collapsed"] [data-sidebar="menu"] {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        width: 100% !important;
      }

      [data-collapsible="icon"] [data-sidebar="menu-item"],
      [data-state="collapsed"] [data-sidebar="menu-item"] {
        width: 100% !important;
        display: flex !important;
        justify-content: center !important;
      }

      /* Deployed-build guard: on tablet/narrow screens keep the sidebar icon-only.
         This prevents the full 17rem sidebar from squeezing dashboard pages like in production. */
      @media (max-width: 1024px) {
        [data-sidebar-collapse-label],
        [data-sidebar-collapse-lock] {
          display: none !important;
        }

        [data-sidebar-collapse-center] {
          justify-content: center !important;
          gap: 0 !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        [data-sidebar-collapse-content] {
          padding-left: 0 !important;
          padding-right: 0 !important;
        }

        [data-sidebar-collapse-header] {
          border-color: transparent !important;
          background: transparent !important;
        }

        [data-sidebar-collapse-menu-button] {
          justify-content: center !important;
          gap: 0 !important;
          overflow: hidden !important;
          width: 2.75rem !important;
          min-width: 2.75rem !important;
          max-width: 2.75rem !important;
          height: 2.75rem !important;
          margin-left: auto !important;
          margin-right: auto !important;
        }
      }

      @media (max-width: 640px) {
        [data-sidebar-collapse-menu-button] {
          width: 2.625rem !important;
          min-width: 2.625rem !important;
          max-width: 2.625rem !important;
          height: 2.625rem !important;
        }
      }

      /* Production fallback: keeps collapsed sidebar icon-only even when SidebarMenuButton
         internal group-data styles are not emitted in the hosted CSS bundle. */
      [data-collapsible="icon"] [data-sidebar-collapse-label],
      [data-state="collapsed"] [data-sidebar-collapse-label] {
        display: none !important;
      }

      [data-collapsible="icon"] [data-sidebar-collapse-lock],
      [data-state="collapsed"] [data-sidebar-collapse-lock] {
        display: none !important;
      }

      [data-collapsible="icon"] [data-sidebar-collapse-center],
      [data-state="collapsed"] [data-sidebar-collapse-center] {
        justify-content: center !important;
        gap: 0 !important;
      }

      [data-collapsible="icon"] [data-sidebar-collapse-content],
      [data-state="collapsed"] [data-sidebar-collapse-content] {
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      [data-collapsible="icon"] [data-sidebar-collapse-header],
      [data-state="collapsed"] [data-sidebar-collapse-header] {
        border-color: transparent !important;
        background: transparent !important;
      }

      [data-collapsible="icon"] [data-sidebar-collapse-menu-button],
      [data-state="collapsed"] [data-sidebar-collapse-menu-button] {
        display: grid !important;
        place-items: center !important;
        justify-content: center !important;
        align-items: center !important;
        gap: 0 !important;
        overflow: hidden !important;
        width: 2.75rem !important;
        min-width: 2.75rem !important;
        max-width: 2.75rem !important;
        height: 2.75rem !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin-left: auto !important;
        margin-right: auto !important;
      }

      [data-sidebar-collapse-menu-button] svg {
        flex-shrink: 0 !important;
      }

      .ig-sidebar-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: rgba(255,255,255,.18) transparent;
      }

      .ig-sidebar-scrollbar::-webkit-scrollbar {
        width: 6px;
      }

      .ig-sidebar-scrollbar::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,.16);
        border-radius: 999px;
      }

      @media (max-width: 640px) {
        body {
          overflow-x: hidden;
        }

        [data-radix-popper-content-wrapper] {
          max-width: calc(100vw - 1rem) !important;
        }
      }
    `}</style>
  );
}

/* -------------------------- helpers -------------------------- */

function normalizePlan(plan) {
  const key = (plan || 'free').toString().trim().toLowerCase();
  if (PLAN_META[key]) return key;
  if (key.includes('pro')) return 'pro';
  if (key.includes('exec')) return 'executive';
  if (key.includes('start')) return 'starter';
  return 'free';
}
function normalizeSubscriptionStatus(status) {
  return (status || '').toString().trim().toLowerCase();
}
function isInactiveSubscriptionStatus(status) {
  const key = normalizeSubscriptionStatus(status);
  return ['expired', 'canceled', 'cancelled', 'past_due', 'inactive', 'ended', 'free'].includes(key);
}


const getAccessTokenSafe = () => {
  try {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('access_token') ||
      sessionStorage.getItem('token') ||
      ''
    );
  } catch { return ''; }
};

function normalizeGamificationUser(payload) {
  const u = payload?.user || payload?.data?.user || payload?.profile || payload || {};
  return {
    rank: u.rank || u.rank_title || u.rankTitle || 'Beginner',
    level: Number(u.level ?? 1) || 1,
    xp: Number(u.xp ?? 0) || 0,
    nextLevelXp: Number(u.next_level_xp ?? u.nextLevelXp ?? 1000) || 1000,
    streak: Number(u.current_streak ?? u.streak ?? 0) || 0,
  };
}

function RankLevelBadge({ profile, loading = false }) {
  const rank = profile?.rank || 'Beginner';
  const level = Number(profile?.level ?? 1) || 1;
  const xp = Number(profile?.xp ?? 0) || 0;
  const nextLevelXp = Math.max(Number(profile?.nextLevelXp ?? 1000) || 1000, 1);
  const pct = Math.max(0, Math.min(100, Math.round((xp / nextLevelXp) * 100)));

  return (
    <div className="hidden md:flex items-center gap-2 rounded-xl border border-amber-300/20 bg-gradient-to-r from-amber-400/10 via-white/[0.04] to-indigo-500/10 px-2.5 py-1.5 shadow-[0_14px_38px_-28px_rgba(251,191,36,0.75)] backdrop-blur-xl">
      <div className="grid h-7 w-7 place-items-center rounded-lg bg-amber-300/15 ring-1 ring-amber-300/20 shrink-0">
        {loading ? <Activity className="h-3.5 w-3.5 animate-pulse text-amber-200" /> : <Trophy className="h-3.5 w-3.5 text-amber-200" />}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5 text-[11px] leading-none text-white/55">
          <span className="truncate max-w-[90px]">{rank}</span>
          <span className="text-white/25">•</span>
          <span className="font-semibold text-white/80">Lv {level}</span>
        </div>
        <div className="mt-1 h-1 w-24 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-300 via-yellow-400 to-indigo-400 transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
}

function LeaderboardTopButton() {
  return (
    <Link
      to="/leaderboard"
      className="hidden sm:inline-flex h-9 sm:h-10 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2.5 sm:px-3 text-xs sm:text-sm font-semibold text-white/80 backdrop-blur-xl transition hover:bg-white/[0.08] hover:text-white shrink-0"
      title="Open leaderboard"
    >
      <Medal className="h-4 w-4 text-amber-200 shrink-0" />
      <span className="hidden lg:inline">Leaderboard</span>
      <ArrowUpRight className="hidden sm:block h-3.5 w-3.5 text-white/45" />
    </Link>
  );
}

const PlanBadge = ({ planKey, compact = false }) => {
  const meta = PLAN_META[planKey] || PLAN_META.free;
  const Icon = meta.icon;
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className={`relative rounded-xl p-[1px] bg-gradient-to-r ${meta.accent} ${meta.glow}`}>
        <div className="flex items-center gap-1.5 sm:gap-2 rounded-xl border border-white/10 bg-black/40 backdrop-blur-xl px-2 sm:px-3 py-1.5">
          <span className="relative inline-flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/60 opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
          <Icon className="h-4 w-4 text-indigo-300 shrink-0" />
          <span className={compact ? 'hidden sm:inline text-xs font-semibold text-white' : 'text-xs font-semibold text-white'}>{meta.name}</span>
          {!compact && <span className="hidden lg:inline text-[11px] text-white/55">• {meta.tag}</span>}
        </div>
      </div>
    </div>
  );
};


/* -------------------------- responsive sidebar safety -------------------------- */

function getMediaQueryMatch(query, fallback = false) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return fallback;
  return window.matchMedia(query).matches;
}

function useSafeMediaQuery(query, fallback = false) {
  const [matches, setMatches] = React.useState(() => getMediaQueryMatch(query, fallback));

  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQueryList = window.matchMedia(query);
    const sync = () => setMatches(mediaQueryList.matches);

    sync();

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', sync);
      return () => mediaQueryList.removeEventListener('change', sync);
    }

    mediaQueryList.addListener(sync);
    return () => mediaQueryList.removeListener(sync);
  }, [query]);

  return matches;
}

/* -------------------------- main shell -------------------------- */

export const RadixSidebarDemo = ({ children }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [currentLanguage, setCurrentLanguage] = React.useState(() => getLanguage());

  React.useEffect(() => subscribeToLanguage(setCurrentLanguage), []);

  const pageDirection = currentLanguage === 'ar' ? 'rtl' : 'ltr';

  // Production fix: the hosted build can initially calculate `useIsMobile()` incorrectly,
  // so the desktop sidebar opens on narrow screens and crushes the dashboard content.
  // This independent media query forces icon-only desktop sidebar below 1024px while
  // still allowing the normal mobile drawer behavior from the shadcn Sidebar internals.
  const forceIconSidebar = useSafeMediaQuery('(max-width: 1024px)');
  const [sidebarOpen, setSidebarOpen] = React.useState(() => {
    if (typeof window === 'undefined') return true;
    return window.matchMedia('(min-width: 1025px)').matches;
  });

  const handleSidebarOpenChange = React.useCallback(
    (nextOpen) => {
      setSidebarOpen(forceIconSidebar ? false : Boolean(nextOpen));
    },
    [forceIconSidebar]
  );

  React.useEffect(() => {
    setSidebarOpen(!forceIconSidebar);
  }, [forceIconSidebar]);

  React.useEffect(() => {
    if (forceIconSidebar) setSidebarOpen(false);
  }, [forceIconSidebar, location.pathname]);

  const user = useSelector((s) => s?.auth?.user);

  const [billingSnapshot, setBillingSnapshot] = React.useState({
    loading: false, plan: null, status: null, renewal: null, seats: null,
  });

  const [gamificationProfile, setGamificationProfile] = React.useState(null);
  const [gamificationLoading, setGamificationLoading] = React.useState(false);

  const _getToken = () => {
    try {
      return (
        localStorage.getItem('access_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('token') ||
        ''
      );
    } catch { return ''; }
  };

  const _formatDate = (val) => {
    if (!val) return '—';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  React.useEffect(() => {
    if (!user) return;
    const token = _getToken();
    const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
    const base = (import.meta?.env?.VITE_API_URL || '').replace(/\/$/, '');
    const candidates = [
      `${base}/api/subscriptions/my-subscription`,
      `${base}/api/subscription/my-subscription`,
      `${base}/api/subscriptions/me`,
      `${base}/api/subscription/me`,
    ];
    let cancelled = false;
    (async () => {
      setBillingSnapshot((p) => ({ ...p, loading: true }));
      try {
        let data = null;
        for (const url of candidates) {
          const res = await fetch(url, { method: 'GET', headers, credentials: 'include' });
          if (res.ok) { data = await res.json(); break; }
        }
        if (!data) {
          if (!cancelled) setBillingSnapshot((p) => ({ ...p, loading: false }));
          return;
        }
        if (data.success === false || data.has_subscription === false) {
          if (!cancelled) {
            setBillingSnapshot({
              loading: false,
              plan: 'free',
              status: data.reason === 'subscription_expired' ? 'expired' : 'free',
              renewal: null, seats: 1,
            });
          }
          return;
        }
        const sub = data.subscription || data.data?.subscription || data.sub || data;
        const planObj = sub?.plan || data?.plan || null;
        const plan =
          (typeof planObj === 'object' ? planObj.plan_type || planObj.name : planObj) ||
          sub?.plan_type || sub?.current_plan || sub?.tier ||
          data?.plan_type || data?.current_plan || data?.tier || 'free';
        const status =
          sub?.status || sub?.subscription_status ||
          data?.status || data?.subscription_status || 'active';
        const renewal =
          sub?.current_period_end || sub?.next_billing_time || sub?.next_billing_date ||
          data?.current_period_end || data?.next_billing_time || data?.next_billing_date || null;
        const seats = sub?.seats || data?.seats || 1;
        if (!cancelled) setBillingSnapshot({ loading: false, plan, status, renewal, seats });
      } catch {
        if (!cancelled) setBillingSnapshot((p) => ({ ...p, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, [user]);


  React.useEffect(() => {
    if (!user) return;
    const token = getAccessTokenSafe();
    if (!token) return;

    const base = (import.meta?.env?.VITE_API_URL || 'http://localhost:5000').replace(/\/$/, '');
    let cancelled = false;

    (async () => {
      setGamificationLoading(true);
      try {
        const res = await fetch(`${base}/api/gamification/gamification-profile`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          credentials: 'include',
        });
        const ct = res.headers.get('content-type') || '';
        const data = ct.includes('application/json') ? await res.json() : null;
        if (!res.ok) throw new Error(data?.error || data?.message || `Gamification request failed (${res.status})`);
        if (!cancelled) setGamificationProfile(normalizeGamificationUser(data));
      } catch (e) {
        console.error('[Sidebar] gamification profile error', e);
        if (!cancelled) setGamificationProfile(null);
      } finally {
        if (!cancelled) setGamificationLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  const userPlan =
    user?.plan || user?.plan_type || user?.current_plan || user?.tier || user?.subscription?.plan_type;
  const userStatus =
    user?.subscription_status || user?.plan_status || user?.status ||
    user?.subscription?.status || user?.subscription?.subscription_status;

  const rawPlanKey = normalizePlan(billingSnapshot.plan || userPlan);
  const rawStatus = billingSnapshot.status || userStatus || (rawPlanKey === 'free' ? 'free' : 'active');
  const hasInactiveStatus = isInactiveSubscriptionStatus(rawStatus);
  const planKey = hasInactiveStatus ? 'free' : rawPlanKey;
  const isFreePlan = planKey === 'free';

  React.useEffect(() => {
    if (!user) return;

    // /chat-interview is the standalone AI Chat setup page and can stay locked for Free users.
    // But when the user comes from /interview-setup-configuration, a Free interview slot
    // was already reserved and the session should continue instead of redirecting to /plans.
    const isRuntimeChatLaunch =
      location.pathname === '/chat-interview' && location.state?.fromSetup === true;

    if (isFreePlan && isFreeLockedPath(location.pathname) && !isRuntimeChatLaunch) {
      navigate('/plans', {
        replace: true,
        state: {
          lockedFeature: true,
          from: location.pathname,
          message: 'Upgrade required to use this feature.',
        },
      });
    }
  }, [user, isFreePlan, location.pathname, location.state, navigate]);

  const headerInfo = getPageHeaderInfo(location.pathname);

  const handleLogout = () => { dispatch(logout()); navigate('/login'); };
  const handleTakeTour = () => { localStorage.removeItem('tour_done'); startTour(); };

  /* -------------------- shared header (desktop + mobile) -------------------- */

  const planMeta = PLAN_META[planKey] || PLAN_META.free;
  const isFreeLike = planKey === 'free' || planKey === 'starter';
  const planStatus = isFreePlan
    ? (hasInactiveStatus && normalizeSubscriptionStatus(rawStatus) !== 'free' ? 'EXPIRED' : 'FREE')
    : rawStatus.toString().toUpperCase();
  const renewalText = _formatDate(billingSnapshot.renewal);
  const seatsText = String(billingSnapshot.seats || 1);
  const sidebarIsIconMode = forceIconSidebar || !sidebarOpen;

  /* -------------------- sidebar contents (extracted) -------------------- */

  const SidebarUserSection = () => {
    const sidebar = useSidebar?.() || {};
    const state = sidebar.state || 'expanded';

    const trigger = (
      <DropdownMenuTrigger className="peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-white/5 hover:text-white focus-visible:ring-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:!p-2 group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0">
        <Avatar className="h-8 w-8 rounded-lg shrink-0">
          <AvatarImage src={user?.avatar_url} alt={user?.name || 'User'} />
          <AvatarFallback className="rounded-lg bg-indigo-500/20 text-indigo-100">
            {String(user?.first_name || user?.email || 'U').slice(0, 1).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div data-sidebar-collapse-label className="grid flex-1 text-left text-sm leading-tight min-w-0 group-data-[collapsible=icon]:hidden">
          <span className="truncate font-semibold">
            {user?.first_name ? `${user.first_name} ${user?.last_name || ''}`.trim() : 'User'}
          </span>
          <span className="truncate text-xs text-white/60">{user?.email || ''}</span>
        </div>
        <Settings data-sidebar-collapse-label className="ml-auto size-4 shrink-0 group-data-[collapsible=icon]:hidden" />
      </DropdownMenuTrigger>
    );

    return (
      <div className="mt-2 group-data-[collapsible=icon]:mt-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
        <DropdownMenu>
          {state === 'collapsed' ? (
            <Tooltip>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent side="right" align="center">User Profile</TooltipContent>
            </Tooltip>
          ) : trigger}
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg bg-[#0B0B12] border-white/10 text-zinc-200"
            side="right" align="end" sideOffset={4}
          >
            <DropdownMenuItem onClick={() => navigate('/my-subscription')} className="focus:bg-white/10 focus:text-white cursor-pointer">
              <CreditCard className="mr-2 size-4" /> Billing & Subscriptions
            </DropdownMenuItem>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuItem onClick={() => navigate('/issue-report')} className="focus:bg-white/10 focus:text-white cursor-pointer">
                  <TriangleAlert className="mr-2 size-4" /> Report an issue
                </DropdownMenuItem>
              </TooltipTrigger>
              <TooltipContent side="right" align="center">
                Bugs, UX/UI feedback, performance, ...
              </TooltipContent>
            </Tooltip>
            <DropdownMenuItem onClick={() => navigate('/setting')} className="focus:bg-white/10 focus:text-white cursor-pointer">
              <Settings className="mr-2 size-4" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="focus:bg-red-500/10 focus:text-red-200 text-red-400 cursor-pointer">
              <LogOut className="mr-2 size-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  return (
    <SidebarProvider
      dir="ltr"
      data-ig-sidebar-shell
      data-sidebar-icon-mode={sidebarIsIconMode ? 'true' : 'false'}
      defaultOpen={!isMobile && !forceIconSidebar}
      open={sidebarOpen}
      onOpenChange={handleSidebarOpenChange}
      className="min-h-screen w-full overflow-x-hidden"
      style={{
        direction: 'ltr',
        '--sidebar-width': sidebarIsIconMode ? 'var(--ig-sidebar-width-icon)' : 'var(--ig-sidebar-width)',
        '--sidebar-width-icon': 'var(--ig-sidebar-width-icon)',
      }}
    >
      <CollapsedSidebarFixStyles />
      <Sidebar data-ig-app-sidebar data-sidebar-icon-mode={sidebarIsIconMode ? 'true' : 'false'} dir="ltr" variant="sidebar" collapsible="icon" className="border-r border-white/10 bg-[#07090c]/95 overflow-hidden shrink-0">
        <div className="relative flex h-full min-h-0 w-full max-w-full flex-col overflow-hidden px-2 group-data-[collapsible=icon]:!w-[var(--ig-sidebar-width-icon)] group-data-[collapsible=icon]:!px-0">
          {/* glow background */}
          <div className="pointer-events-none absolute inset-0 opacity-40">
            <div className="absolute inset-0 bg-gradient-to-b from-white/[0.04] via-transparent to-transparent" />
            <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[90px]" />
            <div className="absolute -bottom-32 -right-28 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[95px]" />
            <div className="absolute top-10 left-1/2 h-[420px] w-[420px] -translate-x-1/2 rounded-full bg-violet-400/8 blur-[110px]" />
            <div className="absolute inset-0 opacity-[0.26] [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:22px_22px]" />
            <div className="absolute inset-0 bg-[radial-gradient(720px_420px_at_20%_10%,rgba(139,92,246,0.22),transparent_58%)]" />
          </div>

          <div className="relative z-10 flex h-full min-h-0 w-full flex-col">
            <SidebarHeader data-sidebar-collapse-header className="border-b border-white/10 bg-black/10 backdrop-blur-xl group-data-[collapsible=icon]:border-none group-data-[collapsible=icon]:bg-transparent">
              <div data-sidebar-collapse-center className="flex w-full items-center gap-3 px-2 py-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0">
                <div className="relative flex h-8 w-8 shrink-0 items-center justify-center group-data-[collapsible=icon]:h-9 group-data-[collapsible=icon]:w-9">
                  <img src="/GenieAI_2_tab.png" alt="InterviewGenieAI" className="h-8 w-8 object-contain" />
                </div>
                <div data-sidebar-collapse-label className="grid flex-1 text-left leading-tight min-w-0 group-data-[collapsible=icon]:hidden">
                  <span className="truncate text-sm font-semibold text-white">InterviewGenieAI</span>
                  <span className="truncate text-xs text-white/60">Professional Assistant</span>
                </div>
              </div>
            </SidebarHeader>

            <SidebarContent data-sidebar-collapse-content className="ig-sidebar-scrollbar min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 group-data-[collapsible=icon]:!px-0 group-data-[collapsible=icon]:!py-2">
              <SidebarGroup className="group-data-[collapsible=icon]:!p-0">
                <SidebarGroupLabel data-sidebar-collapse-label className="text-white/60 group-data-[collapsible=icon]:hidden">Navigation</SidebarGroupLabel>
                <SidebarMenu>
                  {NAV_ITEMS.map((item) => {
                    const isActive =
                      location.pathname === item.path ||
                      (item.path !== '/dashboard-home' && location.pathname.startsWith(item.path));
                    const isLocked = isFreePlan && FREE_LOCKED_NAV_IDS.has(item.id);
                    const IconComp = item.icon;
                    const iconElement = item.isReactIcon ? <IconComp size={20} /> : <IconComp />;
                    const menuClassName =
                      `text-zinc-300 hover:text-white hover:bg-white/5 hover:shadow-[0_0_15px_rgba(255,255,255,0.055)] transition-all duration-300 ease-out data-[active=true]:bg-gradient-to-r data-[active=true]:from-indigo-500/20 data-[active=true]:via-fuchsia-500/15 data-[active=true]:to-cyan-400/15 data-[active=true]:text-white data-[active=true]:shadow-[0_0_22px_rgba(99,102,241,0.22)] data-[active=true]:border data-[active=true]:border-white/10 ${
                        isLocked
                          ? 'opacity-45 cursor-not-allowed hover:bg-transparent hover:text-zinc-300 hover:shadow-none data-[active=true]:bg-transparent data-[active=true]:shadow-none data-[active=true]:border-transparent'
                          : ''
                      }`;

                    return (
                      <SidebarMenuItem key={item.path}>
                        {isLocked ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <SidebarMenuButton
                                id={item.id}
                                isActive={false}
                                tooltip={`${item.title} requires an upgrade`}
                                onClick={(event) => {
                                  event.preventDefault();
                                  navigate('/plans', {
                                    state: {
                                      lockedFeature: true,
                                      from: item.path,
                                      message: `${item.title} is locked on the Free plan.`,
                                    },
                                  });
                                }}
                                className={menuClassName}
                                data-sidebar-collapse-menu-button
                              >
                                {iconElement}
                                <span data-sidebar-collapse-label className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                                <Lock data-sidebar-collapse-lock className="ml-auto h-3.5 w-3.5 shrink-0 text-amber-300/80 group-data-[collapsible=icon]:hidden" />
                              </SidebarMenuButton>
                            </TooltipTrigger>
                            <TooltipContent side="right" align="center">Upgrade required</TooltipContent>
                          </Tooltip>
                        ) : (
                          <SidebarMenuButton asChild id={item.id} isActive={isActive} tooltip={item.title} className={menuClassName} data-sidebar-collapse-menu-button>
                            <Link to={item.path}>
                              {iconElement}
                              <span data-sidebar-collapse-label className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroup>
            </SidebarContent>

            <SidebarFooter className="shrink-0 border-t border-white/10 bg-black/10 backdrop-blur-xl group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:flex-col group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:border-none group-data-[collapsible=icon]:bg-transparent group-data-[collapsible=icon]:!px-0">
              <SidebarGroup className="group-data-[collapsible=icon]:!p-0">
                <SidebarGroupLabel data-sidebar-collapse-label className="text-white/60 group-data-[collapsible=icon]:hidden">More</SidebarGroupLabel>
                <SidebarMenu>
                  {SECONDARY_ITEMS.map((item) => {
                    const isActive = location.pathname.startsWith(item.path);
                    const IconComp = item.icon;
                    return (
                      <SidebarMenuItem key={item.path}>
                        <SidebarMenuButton
                          asChild id={item.id} isActive={isActive} tooltip={item.title}
                          className="text-zinc-400 hover:text-white hover:bg-white/5 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300 ease-out data-[active=true]:bg-gradient-to-r data-[active=true]:from-indigo-500/20 data-[active=true]:via-fuchsia-500/15 data-[active=true]:to-cyan-400/15 data-[active=true]:text-white data-[active=true]:shadow-[0_0_22px_rgba(99,102,241,0.22)] data-[active=true]:border data-[active=true]:border-white/10"
                          data-sidebar-collapse-menu-button
                        >
                          <Link to={item.path}>
                            <IconComp />
                            <span data-sidebar-collapse-label className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}

                  {/* <SidebarMenuItem>
                    <SidebarMenuButton
                      id="menu" onClick={handleTakeTour} tooltip="Take Tour"
                      className="text-zinc-300 hover:bg-white/10 hover:text-white transition-all duration-300 ease-out hover:shadow-[0_0_15px_rgba(255,255,255,0.05)]"
                      data-sidebar-collapse-menu-button
                    >
                      <HelpCircle />
                      <span data-sidebar-collapse-label className="truncate group-data-[collapsible=icon]:hidden">Take Tour</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem> */}

                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={handleLogout} tooltip="Logout"
                      className="text-red-400/80 hover:bg-red-500/10 hover:text-red-200 hover:shadow-[0_0_15px_rgba(239,68,68,0.1)] transition-all duration-300 ease-out"
                      data-sidebar-collapse-menu-button
                    >
                      <LogOut />
                      <span data-sidebar-collapse-label className="truncate group-data-[collapsible=icon]:hidden">Logout</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>

              <div className="mt-2 group-data-[collapsible=icon]:mt-0 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">
                <SidebarUserSection />
              </div>
            </SidebarFooter>
          </div>
        </div>
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="bg-[#0B0B12] relative min-w-0 overflow-x-hidden">
        <AppBackground>
          {/* ── Header — fully responsive ── */}
          <header data-ig-app-header dir="ltr" className="sticky top-0 z-30 flex h-14 sm:h-16 w-full max-w-full min-w-0 shrink-0 items-center gap-1 sm:gap-2 border-b border-white/10 bg-black/20 backdrop-blur-xl overflow-visible">
            {/* Left cluster — title + sidebar toggle */}
            <div className="flex min-w-0 flex-1 items-center gap-1.5 px-2 sm:gap-2 sm:px-4 overflow-hidden">
              <SidebarTrigger className="-ml-1 text-white/80 shrink-0" />
              <Separator orientation="vertical" className="hidden sm:block mr-1 sm:mr-2 h-4 bg-white/10 shrink-0" />
              <div className="flex min-w-0 items-center gap-1.5 sm:gap-2 text-sm font-semibold text-white overflow-hidden">
                {headerInfo.icon && (
                  <AppIcon name={headerInfo.icon} className="h-5 w-5 text-indigo-400 shrink-0" />
                )}
                {headerInfo.iconComponent &&
                  (headerInfo.isReactIcon
                    ? <headerInfo.iconComponent size={20} className="text-indigo-400 shrink-0" />
                    : <headerInfo.iconComponent className="h-5 w-5 text-indigo-400 shrink-0" />)}
                <span className="hidden min-[380px]:inline truncate">{headerInfo.label}</span>
              </div>
            </div>

            {/* Right cluster — Usage + Plan + Upgrade */}
            <div className="flex min-w-0 shrink-0 items-center justify-end gap-1 sm:gap-2 pr-2 sm:pr-4">
              <LanguageSwitcher compact className="inline-flex shrink-0" />
              <RankLevelBadge profile={gamificationProfile} loading={gamificationLoading} />
              <LeaderboardTopButton />
              <div className="hidden sm:block shrink-0">
                <UsageMiniWidget planKey={planKey} />
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger className="outline-none">
                  <div className="group flex items-center gap-1.5 sm:gap-2">
                    <PlanBadge planKey={planKey} compact />
                    <div className="hidden xl:flex items-center gap-1 text-xs text-white/50 group-hover:text-white/70 transition-colors">
                      <span>View</span>
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </div>
                  </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  className="
                    w-[min(380px,calc(100vw-1rem))]
                    max-h-[calc(100vh-5rem)]
                    rounded-2xl bg-[#0B0B12] border-white/10 text-zinc-200 p-0 overflow-y-auto overflow-x-hidden
                  "
                  side="bottom" align="end" sideOffset={10}
                >
                  <div className={`p-4 bg-gradient-to-r ${planMeta.accent}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white">Current Plan</div>
                        <div className="mt-1 text-xl font-bold text-white truncate">{planMeta.name}</div>
                        <div className="mt-1 text-xs text-white/70 truncate">{planMeta.tag}</div>
                      </div>
                      <Link
                        to="/my-subscription"
                        className="text-xs font-semibold text-white/80 hover:text-white px-3 py-1.5 rounded-xl bg-white/10 border border-white/10 hover:bg-white/15 transition shrink-0"
                      >
                        Billing
                      </Link>
                    </div>

                    <div className="mt-3 grid grid-cols-3 gap-2">
                      <div className="rounded-xl bg-black/25 border border-white/10 p-2 min-w-0">
                        <div className="text-[11px] text-white/60">Status</div>
                        <div className="text-sm font-semibold text-white truncate">{planStatus || '—'}</div>
                      </div>
                      <div className="rounded-xl bg-black/25 border border-white/10 p-2 min-w-0">
                        <div className="text-[11px] text-white/60">Renewal</div>
                        <div className="text-sm font-semibold text-white truncate">{renewalText}</div>
                      </div>
                      <div className="rounded-xl bg-black/25 border border-white/10 p-2 min-w-0">
                        <div className="text-[11px] text-white/60">Seats</div>
                        <div className="text-sm font-semibold text-white truncate">{seatsText}</div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="text-xs font-semibold text-white/70 mb-2">What you get</div>
                    <div className="space-y-2">
                      {planMeta.perks.map((p) => (
                        <div key={p} className="flex items-center gap-2 text-sm text-white/75">
                          <span className="h-1.5 w-1.5 rounded-full bg-indigo-400/80 shrink-0" />
                          <span className="truncate">{p}</span>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 flex items-center gap-2">
                      <Link
                        to={isFreeLike ? '/plans' : '/my-subscription'}
                        className="flex-1 inline-flex items-center justify-center gap-2 h-10 rounded-xl text-sm font-semibold text-white
                          bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500
                          shadow-[0_14px_40px_-24px_rgba(99,102,241,0.9)] ring-1 ring-white/10 transition"
                      >
                        {planMeta.cta}
                        <ArrowUpRight className="h-4 w-4" />
                      </Link>
                      {isFreeLike && (
                        <Link
                          to="/plans"
                          className="inline-flex items-center justify-center h-10 px-3 rounded-xl text-sm font-semibold
                            bg-white/5 border border-white/10 text-white/70 hover:text-white hover:bg-white/10 transition shrink-0"
                        >
                          Compare
                        </Link>
                      )}
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Upgrade pill — only on wide enough viewports */}
              <Link
                to="/plans"
                className="hidden xl:inline-flex items-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold text-white
                  bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500
                  shadow-[0_14px_40px_-26px_rgba(99,102,241,0.9)] ring-1 ring-white/10 transition shrink-0"
              >
                Upgrade
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </header>

          {/* Page body */}
          <div data-ig-page-content dir={pageDirection} className="relative z-10 flex flex-1 flex-col gap-4 p-0 min-w-0">
            <div className="w-full min-w-0">{children}</div>
          </div>
        </AppBackground>
      </SidebarInset>
    </SidebarProvider>
  );
}; 
